import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Copy,
  Save,
  KeyRound,
  CheckCircle2,
  Settings2,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { temperatureLabel, reasoningLabel } from "./modelLabels";
import {
  request,
  type Bootstrap,
  type Connection,
  type Model,
  type Obj,
  money,
  label,
} from "./api";
import {
  Modal,
  Field,
  ErrorBox,
  Loading,
  Badge,
  CheckLine,
  Empty,
  Success,
} from "./ui";

const defaults = {
  name: "",
  connection_id: "",
  model_id: "",
  temperature: null as number | null,
  top_p: null as number | null,
  max_output_tokens: 1024,
  streaming: false,
  thinking_budget: null as number | null,
  reasoning_effort: null as string | null,
  context_window: 128000,
  protocol: "",
  extra_params: {},
  price_input_per_million: null as number | null,
  price_output_per_million: null as number | null,
  price_source: "",
  price_checked_at: "",
};
export function ModelManager({
  data,
  onClose,
  onRefresh,
  onSelect,
  initialId = "",
}: {
  data: Bootstrap;
  onClose: () => void;
  onRefresh: () => void;
  onSelect?: (id: string) => void;
  initialId?: string;
}) {
  const initialProfile = data.models.find((m) => m.id === initialId);
  const [selected, setSelected] = useState<string>(initialId);
  const [form, setForm] = useState<Obj>({
    ...defaults,
    connection_id:
      data.connections.find((c) => c.provider !== "demo")?.id ||
      data.connections[0]?.id ||
      "",
    ...initialProfile,
  });
  const [search, setSearch] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState<Obj | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [showSpecialized, setShowSpecialized] = useState(false);
  const [extra, setExtra] = useState(JSON.stringify(initialProfile?.extra_params || {}, null, 2));
  const conn = data.connections.find((c) => c.id === form.connection_id);
  const catalog = useQuery({
    queryKey: ["bench-catalog", form.connection_id],
    queryFn: () =>
      request<{
        models: Obj[];
        source?: string;
        error?: string;
        fetched_at?: string;
      }>("/connections/" + form.connection_id + "/catalog"),
    enabled: !!form.connection_id,
    retry: false,
    staleTime: 60000,
  });
  const caps = useQuery({
    queryKey: [
      "bench-caps",
      form.connection_id,
      form.model_id,
      form.reasoning_effort,
      form.thinking_budget,
      form.streaming,
    ],
    queryFn: () =>
      request(
        "/capabilities?connection_id=" +
          encodeURIComponent(form.connection_id) +
          "&model_id=" +
          encodeURIComponent(form.model_id) +
          "&streaming=" +
          String(form.streaming) +
          (form.reasoning_effort
            ? "&reasoning_effort=" + encodeURIComponent(form.reasoning_effort)
            : "") +
          (form.thinking_budget != null
            ? "&thinking_budget=" + form.thinking_budget
            : ""),
      ),
    enabled: !!form.connection_id && !!form.model_id,
    retry: false,
    staleTime: 60000,
  });
  const cap = caps.data?.capabilities || caps.data;
  const appliedCapabilities = useRef("");
  const issues: { message: string; field?: string; value?: unknown }[] = [];
  for (const field of ["temperature", "top_p"]) {
    const knob = cap?.[field],
      value = form[field];
    if (value != null && knob?.supported === false)
      issues.push({
        field,
        value: null,
        message:
          (field === "temperature" ? "Temperature" : "Top-p") +
          (knob?.ignored
            ? " is ignored by this model. Choose provider default for these saved settings."
            : " is unavailable with these settings. Choose provider default" +
              (cap?.reasoning?.options?.includes("none") ? " or switch reasoning to Off." : ".")),
      });
    else if (value != null && knob && (value < knob.min || value > knob.max))
      issues.push({
        field,
        value: null,
        message:
          field + " must be between " + knob.min + " and " + knob.max + ".",
      });
  }
  if (form.reasoning_effort && cap?.reasoning?.supported === false)
    issues.push({
      field: "reasoning_effort",
      value: null,
      message:
        "Use the provider default for reasoning effort, or a supported thinking budget.",
    });
  if (form.thinking_budget != null && cap?.thinking_budget?.supported === false)
    issues.push({
      field: "thinking_budget",
      value: null,
      message:
        "Use the provider default for thinking budget, or a supported reasoning effort.",
    });
  if (form.thinking_budget != null && form.reasoning_effort)
    issues.push({
      message: "Choose reasoning effort or a thinking budget, not both.",
    });
  if (form.max_output_tokens < 1)
    issues.push({ message: "Set maximum output tokens to at least 1." });
  if (cap?.output?.max && form.max_output_tokens > cap.output.max)
    issues.push({
      field: "max_output_tokens",
      value: cap.output.max,
      message:
        "Set maximum output tokens within this model’s " +
        cap.output.max.toLocaleString() +
        " token limit.",
    });
  if (
    form.protocol &&
    cap?.protocols?.length &&
    !cap.protocols.includes(form.protocol)
  )
    issues.push({
      field: "protocol",
      value: cap.default_protocol,
      message: "Select a protocol this provider supports.",
    });
  const current = data.models.find((m) => m.id === selected);
  const dirty =
    !current ||
    JSON.stringify({ ...defaults, ...current }) !== JSON.stringify(form) ||
    extra !== JSON.stringify(current?.extra_params || {}, null, 2);
  const patch = (key: string, value: unknown) =>
    setForm((f) => ({ ...f, [key]: value }));
  function choose(m: Model) {
    setSelected(m.id);
    setForm({ ...defaults, ...m });
    setExtra(JSON.stringify(m.extra_params || {}, null, 2));
    setTest(null);
    setError(null);
  }
  function fresh() {
    appliedCapabilities.current = "";
    setSelected("");
    setForm({ ...defaults, connection_id: form.connection_id });
    setExtra("{}");
    setTest(null);
    setError(null);
  }
  useEffect(() => {
    if (
      cap &&
      form.model_id &&
      !selected &&
      appliedCapabilities.current !== form.connection_id + ":" + form.model_id
    ) {
      appliedCapabilities.current = form.connection_id + ":" + form.model_id;
      setForm((f) => ({
        ...f,
        protocol:
          f.protocol || cap.default_protocol || (cap.protocols || [])[0] || "",
        context_window: cap.context_window || f.context_window,
        max_output_tokens: Math.min(
          f.max_output_tokens,
          cap.output?.max || f.max_output_tokens,
        ),
        price_input_per_million:
          f.price_input_per_million ?? cap.pricing?.input ?? null,
        price_output_per_million:
          f.price_output_per_million ?? cap.pricing?.output ?? null,
        price_source: f.price_source || cap.pricing?.source || "",
        price_checked_at: f.price_checked_at || cap.pricing?.checked_at || "",
      }));
    }
  }, [cap, form.model_id, selected]);
  async function save() {
    if (issues.length) {
      setError(issues.map((i) => i.message).join(" "));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const body: Obj = { ...form, extra_params: JSON.parse(extra) };
      delete body.id;
      const m = await request<Model>(
        "/models" + (selected ? "/" + selected : ""),
        body,
        selected ? "PATCH" : "POST",
      );
      setSelected(m.id);
      setForm({ ...defaults, ...m });
      onRefresh();
      toast.success("Model saved");
      return m;
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function runTest() {
    setError(null);
    setBusy(true);
    try {
      setTest(await request("/models/" + selected + "/test", {}));
      onRefresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const models = data.models.filter((m) =>
    (
      m.name +
      " " +
      m.model_id +
      " " +
      data.connections.find((c) => c.id === m.connection_id)?.name
    )
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  return (
    <Modal
      title="Saved Models"
      description="A saved model includes its settings. Save different configurations to compare their results."
      onClose={onClose}
      wide
    >
      <div className="model-workbench">
        <aside className="profile-list">
          <div className="b-search">
            <Search size={15} />
            <input
              aria-label="Search Saved Models"
              placeholder="Search saved models"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="b-button full" onClick={fresh}>
            <Plus size={15} />
            New Model
          </button>
          <div className="b-overline">Saved Models · {models.length}</div>
          {models.map((m) => (
            <button
              key={m.id}
              className={"profile-row " + (selected === m.id ? "selected" : "")}
              onClick={() => choose(m)}
            >
              <strong>{m.name}</strong>
              <code>{m.model_id}</code>
              <span>
                {data.connections.find((c) => c.id === m.connection_id)?.name} ·{" "}
                {temperatureLabel(m, data.connections.find(c => c.id === m.connection_id)?.provider)}
                {" · " + reasoningLabel(m)}
              </span>
              {m.demonstration && <Badge>Local Demo</Badge>}
            </button>
          ))}
          {!models.length && (
            <Empty title="No Saved Models">
              Select a connection and model, then save its settings.
            </Empty>
          )}
        </aside>
        <section className="profile-editor">
          <div className="section-toolbar">
            <div>
              <h3>{selected ? "Edit Model" : "New Model"}</h3>
              <p>Each comparison saves a copy of the model and its settings.</p>
            </div>
            {selected && (
              <button
                className="b-button"
                onClick={() => {
                  setSelected("");
                  patch("name", form.name + " · copy");
                  setTest(null);
                }}
              >
                <Copy size={14} />
                Copy Model
              </button>
            )}
          </div>
          <ErrorBox error={error} />
          <div className="b-grid two">
            <Field label="Saved Model Name">
              <input
                value={form.name}
                onChange={(e) => patch("name", e.target.value)}
                placeholder="Gemini · low thinking"
              />
            </Field>
            <Field label="Connection">
              <select
                value={form.connection_id}
                onChange={(e) => {
                  patch("connection_id", e.target.value);
                  patch("model_id", "");
                  patch("protocol", "");
                  setTest(null);
                }}
              >
                {data.connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {conn?.requires_key && !conn.keys.some((k) => k.available) && (
            <div className="b-alert warning">
              Add an available API key in Connections to use this model.
            </div>
          )}
          <div className="catalog-box">
            <div className="section-toolbar">
              <strong>Available Models</strong>
              <button
                className="b-link"
                onClick={() => catalog.refetch()}
                disabled={catalog.isFetching}
              >
                Refresh Models
              </button>
            </div>
            <div className="b-search">
              <Search size={15} />
              <input
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Find a model by name or exact ID"
                aria-label="Search Available Models"
              />
            </div>
            {catalog.data?.error ? (
              <p className="b-muted">
                {catalog.data.error} Enter an exact model ID below or check the
                connection.
              </p>
            ) : catalog.isLoading ? (
              <Loading text="Loading available models…" />
            ) : catalog.error ? (
              <p className="b-muted">
                Catalog unavailable: {String(catalog.error)}. An exact custom ID
                can still be entered below.
              </p>
            ) : (
              <div className="catalog-results">
                {(catalog.data?.models || [])
                  .filter(
                    (m) =>
                      showSpecialized || m.text_benchmark_supported !== false,
                  )
                  .filter((m) =>
                    (m.id + " " + (m.name || ""))
                      .toLowerCase()
                      .includes(catalogSearch.toLowerCase()),
                  )
                  .map((m) => (
                    <button
                      key={m.id}
                      className={form.model_id === m.id ? "selected" : ""}
                      onClick={() => {
                        patch("model_id", m.id);
                        patch("protocol", "");
                        patch("price_input_per_million", null);
                        patch("price_output_per_million", null);
                        patch("price_source", "");
                        patch("price_checked_at", "");
                        if (!form.name) patch("name", m.name || m.id);
                        setTest(null);
                      }}
                    >
                      <span>{m.name || m.id}</span>
                      <code>{m.id}</code>
                      {form.model_id === m.id && <CheckCircle2 size={15} />}
                    </button>
                  ))}
              </div>
            )}
            <CheckLine checked={showSpecialized} onChange={setShowSpecialized}>
              Show Audio, Image, and Other Models
            </CheckLine>
            <small>
              Some listed models cannot generate text. Save the model, then test
              generation to check support.
            </small>
          </div>
          <Field label="Provider Model ID">
            <input
              className="mono"
              value={form.model_id}
              onChange={(e) => {
                patch("model_id", e.target.value);
                setTest(null);
              }}
              placeholder="Enter or select a model ID"
            />
          </Field>
          {caps.isFetching && <Loading text="Checking supported settings…" />}
          {cap && (
            <div className="capability-heading">
              <Badge tone={cap.status === "verified" ? "teal" : "warning"}>
                {cap.status === "verified"
                  ? "Documented Settings"
                  : "Custom Model · Support Unverified"}
              </Badge>
              {cap.source_url && (
                <a href={cap.source_url} target="_blank" rel="noreferrer">
                  View Provider Docs <ExternalLink size={12} />
                </a>
              )}
            </div>
          )}
          <ErrorBox error={caps.error} />
          {issues.length > 0 && (
            <div className="b-alert warning">
              <div>
                <strong>Review Settings Before Saving</strong>
                {issues.map((issue, i) => (
                  <div className="parameter-issue" key={i}>
                    <span>{issue.message}</span>
                    {issue.field && (
                      <button
                        className="b-link"
                        onClick={() => patch(issue.field!, issue.value)}
                      >
                        {issue.field === "max_output_tokens"
                          ? "Use Allowed Maximum"
                          : issue.field === "protocol"
                            ? "Use Supported Protocol"
                            : "Use Provider Default"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="b-grid two">
            <Field
              label="Temperature"
              hint={
                cap?.temperature?.reason ||
                "Lower values usually give more consistent responses. Provider default leaves this setting unset."
              }
            >
              <div className="parameter-row">
                <select
                  value={form.temperature == null ? "default" : "custom"}
                  onChange={(e) =>
                    patch(
                      "temperature",
                      e.target.value === "default" ? null : 0.2,
                    )
                  }
                >
                  <option value="default">Provider Default</option>
                  <option
                    value="custom"
                    disabled={cap?.temperature?.supported === false}
                  >
                    Set Value
                  </option>
                </select>
                {form.temperature != null && (
                  <input
                    aria-label="Temperature Value"
                    type="number"
                    disabled={cap?.temperature?.supported === false}
                    min={cap?.temperature?.min ?? 0}
                    max={cap?.temperature?.max ?? 2}
                    step="0.1"
                    value={form.temperature}
                    onChange={(e) =>
                      patch("temperature", Number(e.target.value))
                    }
                  />
                )}
              </div>
            </Field>
            <Field
              label="Reasoning Effort"
              hint={
                cap?.reasoning?.reason ||
                "Adjust reasoning effort where supported, or use the provider’s default."
              }
            >
              <select
                value={form.reasoning_effort || ""}
                onChange={(e) =>
                  patch("reasoning_effort", e.target.value || null)
                }
              >
                <option value="">Provider Default</option>
                {(cap?.reasoning?.options || []).map((o: string) => (
                  <option
                    key={o}
                    value={o}
                    disabled={cap?.reasoning?.supported === false}
                  >
                    {o === "none" ? "Off" : label(o)}
                  </option>
                ))}
              </select>
              {cap?.reasoning?.supported == null && (
                <input
                  placeholder="Custom effort value"
                  value={form.reasoning_effort || ""}
                  onChange={(e) =>
                    patch("reasoning_effort", e.target.value || null)
                  }
                />
              )}
            </Field>
            {cap?.thinking_budget?.supported !== false && (
              <Field
                label="Thinking Token Budget"
                hint={
                  cap?.thinking_budget?.supported == null
                    ? "Support unverified; check provider documentation."
                    : "Blank uses provider default. " +
                      (cap.thinking_budget.allow_dynamic
                        ? "−1 = dynamic. "
                        : "") +
                      (cap.thinking_budget.allow_off ? "0 = off. " : "") +
                      "Range " +
                      cap.thinking_budget.min +
                      "–" +
                      cap.thinking_budget.max
                }
              >
                <input
                  type="number"
                  step="1"
                  placeholder="Provider default"
                  value={form.thinking_budget ?? ""}
                  onChange={(e) =>
                    patch(
                      "thinking_budget",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                />
              </Field>
            )}
            <Field
              label="Maximum Output Tokens"
              hint={
                cap?.output?.max
                  ? "Provider maximum: " + cap.output.max.toLocaleString()
                  : "A ceiling for the generated response, including reasoning where applicable."
              }
            >
              <input
                type="number"
                min="1"
                max={cap?.output?.max}
                value={form.max_output_tokens}
                onChange={(e) =>
                  patch("max_output_tokens", Number(e.target.value))
                }
              />
            </Field>
            <Field
              label="API Protocol"
              hint="Select the API protocol this model supports. Scribe uses this choice without changing it."
            >
              <select
                value={form.protocol}
                onChange={(e) => patch("protocol", e.target.value)}
              >
                <option value="">Select Protocol</option>
                {(cap?.protocols?.length
                  ? cap.protocols
                  : [
                      "chat_completions",
                      "responses",
                      "messages",
                      "generate_content",
                    ]
                ).map((p: string) => (
                  <option value={p} key={p}>
                    {label(p)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {cap?.pricing && (
            <button
              className="b-link"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  price_input_per_million: cap.pricing.input,
                  price_output_per_million: cap.pricing.output,
                  price_source: cap.pricing.source,
                  price_checked_at: cap.pricing.checked_at,
                }))
              }
            >
              Use Documented Rates · Checked {cap.pricing.checked_at}
            </button>
          )}
          {cap?.notes?.map((n: string) => (
            <p className="b-muted" key={n}>
              {n}
            </p>
          ))}
          <CheckLine
            checked={!!form.streaming}
            onChange={(v) => patch("streaming", v)}
          >
            Stream Responses{" "}
            {cap?.streaming === false
              ? "(unsupported; turn off before saving)"
              : ""}
          </CheckLine>
          <div className="price-summary">
            Estimated token rates:{" "}
            {form.price_input_per_million == null
              ? "Input not set"
              : money(form.price_input_per_million) + " input / 1M"}{" "}
            ·{" "}
            {form.price_output_per_million == null
              ? "Output not set"
              : money(form.price_output_per_million) + " output / 1M"}
            {form.price_source && (
              <a href={form.price_source} target="_blank" rel="noreferrer">
                Source · {form.price_checked_at || "Date not recorded"}
              </a>
            )}
          </div>
          <button
            className="b-link advanced-toggle"
            onClick={() => setAdvanced(!advanced)}
          >
            <Settings2 size={14} />
            {advanced
              ? "Hide Advanced Settings"
              : "Show Advanced Settings"}
          </button>
          {advanced && (
            <div className="advanced-panel">
              <div className="b-grid two">
                <Field
                  label="Top-p"
                  hint={
                    cap?.top_p?.reason ||
                    "Leave blank for provider default. Avoid changing temperature and top-p together."
                  }
                >
                  <input
                    type="number"
                    disabled={cap?.top_p?.supported === false}
                    min={cap?.top_p?.min ?? 0}
                    max={cap?.top_p?.max ?? 1}
                    step="0.05"
                    value={form.top_p ?? ""}
                    onChange={(e) =>
                      patch(
                        "top_p",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Field>
                <Field label="Context Window">
                  <input
                    type="number"
                    min="1"
                    value={form.context_window}
                    onChange={(e) =>
                      patch("context_window", Number(e.target.value))
                    }
                  />
                </Field>
                <Field label="Input Price · USD / 1M Tokens">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price_input_per_million ?? ""}
                    onChange={(e) =>
                      patch(
                        "price_input_per_million",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Field>
                <Field label="Output Price · USD / 1M Tokens">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price_output_per_million ?? ""}
                    onChange={(e) =>
                      patch(
                        "price_output_per_million",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Field>
                <Field label="Price Source">
                  <input
                    value={form.price_source || ""}
                    onChange={(e) => patch("price_source", e.target.value)}
                  />
                </Field>
                <Field label="Price Checked On">
                  <input
                    type="date"
                    value={form.price_checked_at || ""}
                    onChange={(e) => patch("price_checked_at", e.target.value)}
                  />
                </Field>
              </div>
              <Field
                label="Additional Parameters · JSON"
                hint="Use supported parameter combinations. Save API keys in Connections, never here."
              >
                <textarea
                  className="mono"
                  rows={4}
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                />
              </Field>
            </div>
          )}
          {test?.status === "failed" && (
            <ErrorBox error={test.message || "Generation failed. Check the key and model settings, then try again."} />
          )}{" "}
          {test && test.status !== "failed" && (
            <Success>
              Test response:{" "}
              {test.text ||
                test.response?.text ||
                test.message ||
                label(test.status || "complete")}{" "}
              {test.cost_usd != null
                ? "Estimated cost " + money(test.cost_usd)
                : ""}
            </Success>
          )}
          <p className="b-muted">
            Tests make paid provider requests with up to 2,048 output tokens.
            Their costs are separate from comparison budgets.
          </p>
          <div className="modal-actions sticky">
            <span className="b-muted">
              {dirty ? "Unsaved settings" : "Model saved"}
            </span>
            <button
              className="b-button"
              disabled={!selected || dirty || busy}
              onClick={runTest}
            >
              Test Generation
            </button>
            <button
              className="b-button primary"
              disabled={
                busy ||
                caps.isFetching ||
                issues.length > 0 ||
                !form.name ||
                !form.model_id ||
                !form.connection_id
              }
              onClick={() => save()}
            >
              <Save size={15} />
              Save Model
            </button>
            {onSelect && (
              <button
                className="b-button primary"
                disabled={busy || caps.isFetching || issues.length > 0}
                onClick={async () => {
                  const m = dirty ? await save() : current;
                  if (m) {
                    onSelect(m.id);
                    onClose();
                  }
                }}
              >
                Use Model
              </button>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

const connectionProviders = [
  { id: "openai", name: "OpenAI", company: "GPT models", mark: "O" },
  {
    id: "anthropic",
    name: "Anthropic Claude",
    company: "Claude models",
    mark: "A",
  },
  { id: "gemini", name: "Google Gemini", company: "Gemini models", mark: "G" },
  { id: "xai", name: "xAI Grok", company: "Grok models", mark: "x" },
  {
    id: "compatible",
    name: "Custom Provider",
    company: "OpenAI-compatible API",
    mark: "+",
  },
];
const connectionProviderName = (provider: string) =>
  connectionProviders.find((p) => p.id === provider)?.name ||
  (provider === "demo" ? "Local Demo" : label(provider));
function nextConnectionName(provider: string, connections: Connection[]) {
  const name = connectionProviderName(provider);
  if (!connections.some((c) => c.name === name)) return name;
  let n = 2;
  while (connections.some((c) => c.name === `${name} ${n}`)) n++;
  return `${name} ${n}`;
}
function isLocalEndpoint(url: string) {
  try {
    return ["localhost", "127.0.0.1", "[::1]", "::1"].includes(
      new URL(url).hostname,
    );
  } catch {
    return false;
  }
}

export function Connections({
  data,
  onClose,
  onRefresh,
}: {
  data: Bootstrap;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [provider, setProvider] = useState("");
  const [targetId, setTargetId] = useState("new");
  const [apiKey, setApiKey] = useState("");
  const [keyLabel, setKeyLabel] = useState("");
  const [storage, setStorage] = useState("os_keychain");
  const [connectionName, setConnectionName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [quotaGroup, setQuotaGroup] = useState("");
  const [maxConcurrency, setMaxConcurrency] = useState(8);
  const [recent, setRecent] = useState<Connection | null>(null);
  const [edit, setEdit] = useState<Obj | null>(null);
  const [error, setError] = useState<unknown>();
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const keyInput = useRef<HTMLInputElement>(null);
  const formTop = useRef<HTMLDivElement>(null);
  const connections =
    recent && !data.connections.some((c) => c.id === recent.id)
      ? [...data.connections, recent]
      : data.connections.map((c) => (c.id === recent?.id ? recent : c));
  const target = connections.find((c) => c.id === targetId);
  const providerConnections = connections.filter(
    (c) => c.provider === provider,
  );
  const autoName = nextConnectionName(provider, connections);
  const localWithoutKey =
    provider === "compatible" && isLocalEndpoint(target?.base_url || baseUrl);
  const canSave =
    !!provider &&
    !busy &&
    (targetId !== "new" || provider !== "compatible" || !!baseUrl.trim()) &&
    (!!apiKey.trim() || (targetId === "new" && localWithoutKey));
  const keychainFailed =
    storage === "os_keychain" &&
    String(error instanceof Error ? error.message : error || "")
      .toLowerCase()
      .includes("credential store");
  const available = (c: Connection) =>
    !c.requires_key || c.keys.some((k) => k.available);
  function chooseProvider(next: string, connection?: Connection) {
    const existing =
      connection ||
      (next !== "compatible"
        ? connections.find((c) => c.provider === next && !c.base_url)
        : undefined);
    setProvider(next);
    setTargetId(existing?.id || "new");
    setApiKey("");
    setKeyLabel("");
    setStorage("os_keychain");
    setConnectionName("");
    setBaseUrl("");
    setQuotaGroup("");
    setMaxConcurrency(8);
    setError(null);
    setSuccess("");
    setEdit(null);
    if (connection)
      formTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => keyInput.current?.focus(), 0);
  }
  async function saveKey(mode = storage) {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    setSuccess("");
    try {
      const key = apiKey.trim();
      const name = connectionName.trim() || autoName;
      const body = target
        ? {
            api_key: key,
            label: keyLabel.trim() || `API key ${target.keys.length + 1}`,
            storage_mode: mode,
          }
        : {
            provider,
            name,
            base_url: provider === "compatible" ? baseUrl.trim() : null,
            quota_group:
              quotaGroup.trim() ||
              providerConnections[0]?.quota_group ||
              provider,
            max_concurrency: maxConcurrency,
            ...(key
              ? {
                  api_key: key,
                  key_label: keyLabel.trim() || "API key",
                  storage_mode: mode,
                }
              : {}),
          };
      const result = await request<Connection>(
        target ? `/connections/${target.id}/keys` : "/connections",
        body,
      );
      setRecent(result);
      setTargetId(result.id);
      setApiKey("");
      setKeyLabel("");
      setStorage(mode);
      onRefresh();
      setSuccess(
        key
          ? `${connectionProviderName(provider)} key saved${mode === "session_only" ? " for this session" : " in Keychain"}.`
          : "Local provider saved.",
      );
      toast.success("Connection saved");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function checkAccess(connection: Connection) {
    setBusy(true);
    setError(null);
    try {
      const checked = await request<Connection>(
        `/connections/${connection.id}/test`,
        {},
      );
      setRecent(checked);
      onRefresh();
      toast.info(
        checked.last_test?.status === "access_checked"
          ? "Account access confirmed"
          : "Account access could not be confirmed. Test a saved model to check generation.",
      );
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function saveMetadata() {
    if (!edit) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await request<Connection>(
        `/connections/${edit.id}`,
        {
          name: edit.name,
          base_url: edit.base_url || null,
          quota_group: edit.quota_group,
          max_concurrency: edit.max_concurrency,
        },
        "PATCH",
      );
      setRecent(updated);
      setEdit(null);
      onRefresh();
      toast.success("Connection settings saved");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Connections"
      description="Connect a provider to use its models. Select a provider, paste your API key, and save."
      onClose={onClose}
      wide
    >
      <div className="connections-simple" ref={formTop}>
        <div className="connection-step">
          <span>1</span>
          <h3>Select Provider</h3>
        </div>
        <div
          className="provider-choices"
          role="group"
          aria-label="Select Provider"
        >
          {connectionProviders.map((p) => (
            <button
              type="button"
              className={
                "provider-choice " + (provider === p.id ? "selected" : "")
              }
              key={p.id}
              aria-pressed={provider === p.id}
              disabled={busy}
              onClick={() => chooseProvider(p.id)}
            >
              <span className="provider-mark" aria-hidden="true">
                {p.mark}
              </span>
              <strong>{p.name}</strong>
              <small>{p.company}</small>
              {provider === p.id && (
                <CheckCircle2 size={16} className="provider-selected" />
              )}
            </button>
          ))}
        </div>
        <ErrorBox error={error} />
        {success && <Success>{success}</Success>}
        {provider ? (
          <form
            className="connection-key-form"
            onSubmit={(e) => {
              e.preventDefault();
              saveKey();
            }}
          >
            <div className="connection-step">
              <span>2</span>
              <h3>Add {connectionProviderName(provider)} API Key</h3>
            </div>
            {targetId === "new" && provider === "compatible" && (
              <Field
                label="API Base URL"
                hint="Use the full base URL for your OpenAI-compatible provider."
              >
                <input
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://your-provider.example/v1"
                  required
                  disabled={busy}
                />
              </Field>
            )}
            <Field
              label={
                localWithoutKey && targetId === "new"
                  ? "API Key · Optional for Local Providers"
                  : "API Key"
              }
            >
              <input
                ref={keyInput}
                type="password"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setSuccess("");
                }}
                autoComplete="new-password"
                spellCheck={false}
                placeholder="Paste your API key"
                disabled={busy}
                required={!localWithoutKey || targetId !== "new"}
              />
            </Field>
            <div className="credential-note">
              <KeyRound size={14} />
              <span>
                {storage === "session_only"
                  ? "Available until Scribe restarts."
                  : "Saved in your computer’s Keychain."}
              </span>
            </div>
            <details className="connection-advanced">
              <summary>
                Advanced Options <span>Optional</span>
              </summary>
              <div className="b-grid two">
                {providerConnections.length > 0 && (
                  <Field label="Save to Connection">
                    <select
                      value={targetId}
                      onChange={(e) => {
                        setTargetId(e.target.value);
                        setError(null);
                      }}
                      disabled={busy}
                    >
                      {providerConnections.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                      <option value="new">Create Another Connection</option>
                    </select>
                  </Field>
                )}
                {targetId === "new" && (
                  <Field label="Connection Name">
                    <input
                      value={connectionName}
                      onChange={(e) => setConnectionName(e.target.value)}
                      placeholder={autoName}
                      disabled={busy}
                    />
                  </Field>
                )}
                <Field label="Key Name">
                  <input
                    value={keyLabel}
                    onChange={(e) => setKeyLabel(e.target.value)}
                    placeholder="Optional name for this key"
                    disabled={busy}
                  />
                </Field>
                <Field label="Key Storage">
                  <select
                    value={storage}
                    onChange={(e) => {
                      setStorage(e.target.value);
                      setError(null);
                    }}
                    disabled={busy}
                  >
                    <option value="os_keychain">
                      Keychain · Keep Across Restarts
                    </option>
                    <option value="session_only">This Session Only</option>
                  </select>
                </Field>
                {targetId === "new" && (
                  <>
                    <Field
                      label="Shared Quota Group"
                      hint="Connections in the same group share their request allowance."
                    >
                      <input
                        value={quotaGroup}
                        onChange={(e) => setQuotaGroup(e.target.value)}
                        placeholder={
                          providerConnections[0]?.quota_group || provider
                        }
                        disabled={busy}
                      />
                    </Field>
                    <Field label="Concurrent Provider Requests">
                      <input
                        type="number"
                        min="1"
                        max="400"
                        value={maxConcurrency}
                        onChange={(e) =>
                          setMaxConcurrency(Number(e.target.value))
                        }
                        disabled={busy}
                      />
                    </Field>
                  </>
                )}
              </div>
            </details>
            <div className="connection-save-row">
              <span>
                {target
                  ? `Adds a key to ${target.name}`
                  : `Creates ${connectionName.trim() || autoName}`}
              </span>
              <button
                className="b-button primary"
                type="submit"
                disabled={!canSave}
              >
                <Save size={15} />
                {busy ? "Saving…" : "Save Key"}
              </button>
            </div>
            {keychainFailed && (
              <div className="session-fallback">
                <p>
                  Keychain could not save the key. Try again, or save it for this
                  session. A session key is lost when Scribe restarts.
                </p>
                <button
                  type="button"
                  className="b-button"
                  disabled={!canSave}
                  onClick={() => saveKey("session_only")}
                >
                  Save for This Session
                </button>
              </div>
            )}
          </form>
        ) : (
          <p className="provider-choice-hint">
            Select a provider above to add its key.
          </p>
        )}
        <section className="saved-connections">
          <div className="section-toolbar">
            <h3>Saved Connections</h3>
            <span className="b-muted">
              {connections.filter((c) => c.provider !== "demo").length}{" "}
              providers / accounts
            </span>
          </div>
          {!connections.length && (
            <p className="b-muted">
              Your connected providers will appear here.
            </p>
          )}
          {connections.map((c) => (
            <details className="saved-connection" key={c.id}>
              <summary>
                <span className="saved-provider-mark" aria-hidden="true">
                  {connectionProviders.find((p) => p.id === c.provider)?.mark ||
                    "D"}
                </span>
                <span className="saved-connection-title">
                  <strong>{c.name}</strong>
                  <small>
                    {connectionProviderName(c.provider)} · {c.keys.length}{" "}
                    {c.keys.length === 1 ? "key" : "keys"}
                  </small>
                </span>
                <Badge
                  tone={
                    c.provider === "demo"
                      ? "neutral"
                      : available(c)
                        ? "teal"
                        : "warning"
                  }
                >
                  {c.provider === "demo"
                    ? "Local Demo"
                    : available(c)
                      ? "Key Available"
                      : "Key Needed"}
                </Badge>
                <span className="saved-manage">Manage</span>
              </summary>
              <div className="saved-connection-body">
                {c.provider !== "demo" && (
                  <div className="saved-connection-actions">
                    <button
                      type="button"
                      className="b-button"
                      onClick={() => chooseProvider(c.provider, c)}
                      disabled={busy}
                    >
                      <Plus size={14} />
                      Add Key
                    </button>
                    <button
                      type="button"
                      className="b-button subtle"
                      disabled={busy || !available(c)}
                      onClick={() => checkAccess(c)}
                    >
                      Check Access
                    </button>
                    <button
                      type="button"
                      className="b-link"
                      onClick={() => {
                        setEdit(edit?.id === c.id ? null : { ...c });
                        setError(null);
                      }}
                      disabled={busy}
                    >
                      Connection Settings
                    </button>
                  </div>
                )}
                {c.keys.map((k) => (
                  <div className="key-row" key={k.id}>
                    <KeyRound size={15} />
                    <div>
                      <strong>{k.label}</strong>
                      <code>{k.masked}</code>
                    </div>
                    <Badge tone={k.available ? "teal" : "warning"}>
                      {k.available ? "Available" : "Unavailable"}
                    </Badge>
                    <small>
                      {k.storage_mode === "session_only"
                        ? "This session only"
                        : "Keychain"}
                    </small>
                    <button
                      type="button"
                      className="b-icon danger"
                      aria-label={"Remove Key " + k.label}
                      disabled={busy}
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `Remove ${k.label} from ${c.name} and the credential store?`,
                          )
                        )
                          return;
                        setBusy(true);
                        setError(null);
                        try {
                          const updated = await request<Connection>(
                            `/connections/${c.id}/keys/${k.id}`,
                            undefined,
                            "DELETE",
                          );
                          setRecent(updated);
                          onRefresh();
                          toast.success("Key removed");
                        } catch (e) {
                          setError(e);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {c.provider === "demo" ? (
                  <p className="b-muted">
                    Scripted local responses. No API key is needed.
                  </p>
                ) : (
                  <p className="b-muted">
                    {c.last_test?.status === "access_checked"
                      ? "Account access checked."
                      : "Saving a key does not test provider access."}{" "}
                    The first available key is used. Added keys share this
                    connection’s quota.
                  </p>
                )}
                {edit?.id === c.id && (
                  <div className="connection-settings">
                    <div className="b-grid two">
                      <Field label="Connection Name">
                        <input
                          value={edit.name}
                          onChange={(e) =>
                            setEdit({ ...edit, name: e.target.value })
                          }
                        />
                      </Field>
                      {c.provider === "compatible" && (
                        <Field label="API Base URL">
                          <input
                            value={edit.base_url || ""}
                            onChange={(e) =>
                              setEdit({ ...edit, base_url: e.target.value })
                            }
                          />
                        </Field>
                      )}
                      <Field label="Shared Quota Group">
                        <input
                          value={edit.quota_group}
                          onChange={(e) =>
                            setEdit({ ...edit, quota_group: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Concurrent Provider Requests">
                        <input
                          type="number"
                          min="1"
                          max="400"
                          value={edit.max_concurrency}
                          onChange={(e) =>
                            setEdit({
                              ...edit,
                              max_concurrency: Number(e.target.value),
                            })
                          }
                        />
                      </Field>
                    </div>
                    <div className="modal-actions">
                      <button
                        type="button"
                        className="b-button"
                        onClick={() => setEdit(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="b-button primary"
                        onClick={saveMetadata}
                        disabled={busy || !edit.name.trim()}
                      >
                        Save Settings
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </details>
          ))}
        </section>
      </div>
    </Modal>
  );
}

export function ProfilePicker({
  data,
  value,
  onChange,
  onManage,
  empty = "Select Model",
  excludeIds = [],
}: {
  data: Bootstrap;
  value: string;
  onChange: (id: string) => void;
  onManage: () => void;
  empty?: string;
  excludeIds?: string[];
}) {
  const [open, setOpen] = useState(false);
  const selected = data.models.find((m) => m.id === value);
  return (
    <div className="profile-picker">
      <button className="profile-picker-trigger" onClick={() => setOpen(true)}>
        <span>{selected?.name || empty}</span>
        <Settings2 size={14} />
      </button>
      {selected && <code className="profile-exact">{selected.model_id}</code>}
      {open && (
        <ProfileSelectDialog
          data={data}
          value={value}
          excludeIds={excludeIds}
          onChange={onChange}
          onManage={onManage}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export function ProfileSelectDialog({
  data,
  value = "",
  onChange,
  onManage,
  onClose,
  excludeIds = [],
}: {
  data: Bootstrap;
  value?: string;
  onChange: (id: string) => void;
  onManage: () => void;
  onClose: () => void;
  excludeIds?: string[];
}) {
  const [query, setQuery] = useState("");
  const choices = useMemo(
    () =>
      data.models.filter((m) =>
        [
          m.name,
          m.model_id,
          data.connections.find((c) => c.id === m.connection_id)?.name,
          temperatureLabel(m, data.connections.find(c => c.id === m.connection_id)?.provider),
          reasoningLabel(m),
          m.thinking_budget,
          m.max_output_tokens,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [data, query],
  );
  return (
    <Modal
      title="Select Model"
      description="Select a saved model to use its settings, or edit a model before selecting it."
      onClose={onClose}
    >
      <div className="b-search">
        <Search size={16} />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search models, settings, or connections"
          aria-label="Search Saved Models"
        />
      </div>
      <div className="picker-list">
        {choices.map((m) => (
          <button
            className={"profile-row " + (value === m.id ? "selected" : "")}
            key={m.id}
            disabled={excludeIds.includes(m.id) && m.id !== value}
            onClick={() => {
              onChange(m.id);
              onClose();
            }}
          >
            <strong>{m.name}</strong>
            <code>{m.model_id}</code>
            <span>
              {data.connections.find((c) => c.id === m.connection_id)?.name} ·{" "}
              {temperatureLabel(m, data.connections.find(c => c.id === m.connection_id)?.provider)}{" "}
              · {reasoningLabel(m)}{" "}
              · {m.max_output_tokens} output tokens
            </span>
            {excludeIds.includes(m.id) && m.id !== value && (
              <Badge>Already Selected</Badge>
            )}
            {m.demonstration && <Badge>Local Demo</Badge>}
          </button>
        ))}
        {!choices.length && (
          <Empty
            title={
              data.models.length
                ? "No Matching Models"
                : "No Saved Models"
            }
          >
            Search by model name or settings, or save a model to select it here.
          </Empty>
        )}
      </div>
      <button
        className="b-button full"
        onClick={() => {
          onClose();
          onManage();
        }}
      >
        <Plus size={15} />
        Edit Saved Models
      </button>
    </Modal>
  );
}
